import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { I18N } from 'aurelia-i18n';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class OutcomesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.string({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public goalId!: string;

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected outcomes: Array<CM.CarePlanOutcome> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 10;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CarePlanService,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!this.outcomes || this.outcomes.length === 0) {
            this.reload();
        }
        else {
            this.resultCount = this.outcomes.length;
            this.outcomes.splice(this.pageSize);
        }
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadOutcomes();
    }

    protected buildCreatedByMessage(outcome: CM.CarePlanOutcome): string | null {
        const stamp = outcome.metadata?.created?.timestamp;
        const byUser = outcome.metadata?.created?.byUser?.description;

        if(outcome.metadata?.triggeredBy?.description) {
            const key = 'ui:stamps.triggeredBy.timestamp'
            const source = outcome.metadata.triggeredBy.description;
            return this.i18n.tr(key, { source: source, timestamp: stamp, user: byUser });
        }
        else {
            const key = 'ui:stamps.created.timestamp'
            return this.i18n.tr(key, { timestamp: stamp, user: byUser });
        }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadOutcomes(): Promise<void> {
        if(!this.goalId) return;

        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };
        try {
            this.state.beginLoad();
            const response = await this.service.loadOutcomesByGoal(this.goalId, options);
            this.outcomes = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
